import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Field,
  Input,
  Spinner,
  Switch,
  Toaster,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Edit24Filled } from "@fluentui/react-icons";
import { RootState } from "../../../redux/store";
import apiServices from "../../../service";
import ASCButton from "../../../components/Button";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { checkUserPermission } from "../../../utils";
import { UserPermissionsList } from "../../../config";

// Writes go to masterdata's new /company-profile/smtp endpoint (WAT-I2539
// follow-up); reads stay on the pre-existing GET /user/info call (which
// middleware itself forwards to user service's smtp/find/ route) — the
// architecture doc's "not moved: findSmtpServers" split, kept on the
// frontend too rather than duplicating a read path that already works.
const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "12px",
  },
  panel: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "20px 22px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    columnGap: "28px",
    rowGap: "20px",
    marginBottom: "18px",
  },
  actions: {
    display: "flex",
    gap: "10px",
  },
});

type SmtpFormValues = {
  serverAddress: string;
  port: string;
  tlsSsl: boolean;
};

function SmtpDetails() {
  const styles = useStyles();
  const { showToast, toasterId } = useToastMessage();

  const authUser = useSelector((state: RootState) => state.authUser);
  const canEdit = checkUserPermission(
    authUser,
    UserPermissionsList.ALLOW_UPDATE_MASTER_DATA
  );

  const [mode, setMode] = useState<"view" | "edit">("view");
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [smtpData, setSmtpData] = useState<{
    ServerAddress: string | null;
    Port: number | null;
    TLS_SSL: boolean | null;
  } | null>(null);

  const getSmtpDetails = async () => {
    setLoading(true);
    try {
      const res: any = await apiServices.credentials.getList();
      const row = Array.isArray(res.data?.data) ? res.data.data[0] : null;
      setSmtpData(
        row
          ? {
              ServerAddress: row.ServerAddress ?? null,
              Port: row.Port ?? null,
              TLS_SSL: row.TLS_SSL ?? null,
            }
          : null
      );
    } catch (err) {
      // No SMTP settings saved yet for this tenant — not an error state,
      // same as Company Details' own first-load 404 handling.
      setSmtpData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSmtpDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik<SmtpFormValues>({
    initialValues: {
      serverAddress: smtpData?.ServerAddress || "",
      port: smtpData?.Port != null ? String(smtpData.Port) : "",
      tlsSsl: smtpData?.TLS_SSL ?? false,
    },
    enableReinitialize: true,
    // Computes errors/isValid against initialValues immediately on mount
    // (and on every reinit), not only after the user first touches/blurs a
    // field — otherwise formik.isValid can read true for an untouched blank
    // mandatory field (WAT-I2688: Save must be disabled from the moment
    // Edit mode opens if a mandatory field is already invalid, not just
    // after the user has interacted with it).
    validateOnMount: true,
    validationSchema: Yup.object({
      serverAddress: Yup.string()
        .required("Server Address is required.")
        // Matches TenantInfo.ServerAddress's real column width (widened
        // from 20 to 255 — 20 was too short for many real SMTP relay
        // hostnames, e.g. AWS SES's email-smtp.us-east-1.amazonaws.com at
        // 35 chars).
        .max(255, "Server Address cannot be longer than 255 characters."),
      port: Yup.number()
        .typeError("Port must be a number.")
        .required("Port is required.")
        .integer("Port must be a whole number.")
        .min(1, "Port must be between 1 and 65535.")
        .max(65535, "Port must be between 1 and 65535."),
      tlsSsl: Yup.boolean(),
    }),
    onSubmit: (values) => {
      handleSave(values);
    },
  });

  const enterEditMode = () => setMode("edit");

  const cancelEdit = () => {
    formik.resetForm();
    setMode("view");
    showToast("Discarded", "Changes discarded.", "warning");
  };

  const handleSave = async (values: SmtpFormValues) => {
    setSaveLoading(true);
    try {
      const res: any = await apiServices.systemManager.companyDetails.saveSmtp({
        ServerAddress: values.serverAddress.trim(),
        Port: Number(values.port),
        TLS_SSL: values.tlsSsl,
      });
      if (res.data?.isError) {
        throw new Error(res.data?.error?.message?.[0] || res.data?.message);
      }
      await getSmtpDetails();
      setMode("view");
      showToast("Saved", "SMTP details saved successfully.");
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message?.[0] ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to save SMTP details.";
      showToast("Error", message, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />

      <div className={styles.toolbar}>
        {mode === "view" && canEdit && (
          <ASCButton
            shape="rounded"
            icon={<Edit24Filled />}
            label="Edit"
            onClick={enterEditMode}
            disabled={loading}
          />
        )}
      </div>

      {loading && !smtpData ? (
        <Spinner label="Loading SMTP details…" />
      ) : (
        <div className={styles.panel}>
          <div className={styles.grid}>
            <Field
              label="Server Address"
              required
              validationState={
                formik.touched.serverAddress && formik.errors.serverAddress
                  ? "error"
                  : undefined
              }
              validationMessage={
                formik.touched.serverAddress
                  ? formik.errors.serverAddress
                  : undefined
              }
            >
              <Input
                name="serverAddress"
                value={formik.values.serverAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={mode === "view"}
                placeholder="e.g. smtp.gmail.com"
              />
            </Field>

            <Field
              label="Port"
              required
              validationState={
                formik.touched.port && formik.errors.port ? "error" : undefined
              }
              validationMessage={formik.touched.port ? formik.errors.port : undefined}
            >
              <Input
                name="port"
                type="number"
                value={formik.values.port}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={mode === "view"}
                placeholder="e.g. 587"
              />
            </Field>

            <Field label="Use TLS/SSL">
              <Switch
                checked={formik.values.tlsSsl}
                onChange={(_, data) =>
                  formik.setFieldValue("tlsSsl", data.checked)
                }
                disabled={mode === "view"}
              />
            </Field>
          </div>

          {mode === "edit" && (
            <div className={styles.actions}>
              <ASCButton
                shape="rounded"
                appearance="primary"
                className="asc-button-primary"
                label={saveLoading ? "Saving…" : "Save"}
                icon={saveLoading ? <Spinner size="tiny" /> : undefined}
                onClick={() => formik.handleSubmit()}
                disabled={saveLoading || !formik.isValid}
              />
              <ASCButton
                shape="rounded"
                label="Cancel"
                onClick={cancelEdit}
                disabled={saveLoading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SmtpDetails;
